/**
 * Date arithmetic for the record and the program view. Commitments carry ISO dates (YYYY-MM-DD) and
 * events carry ISO timestamps.
 *
 * Arithmetic works on the calendar date in UTC so a label such as "due in 3 days" is the same wherever
 * the API happens to run. Display is a separate question and `docs/2026-09-28-displayed-text.md`
 * settles it: a calendar fact (a due date) is a bare date with no zone, and an operational timestamp
 * (a poll, a run) carries the time and always names the zone it was rendered in. `fmtStamp` takes that
 * zone, so a string built in the browser is the viewer's own clock and one built on the server says UTC
 * rather than pretending to be local.
 */
import type { Iso } from "./commitment.js";

const DAY_MS = 86_400_000;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** The calendar date (YYYY-MM-DD) of a timestamp or date. */
export function dateOf(iso: Iso): string {
  return iso.slice(0, 10);
}

/** Whole days from `a` to `b` (positive when `b` is later). Dates only; the time of day does not count. */
export function daysBetween(a: Iso, b: Iso): number {
  return Math.round((Date.parse(`${dateOf(b)}T00:00:00Z`) - Date.parse(`${dateOf(a)}T00:00:00Z`)) / DAY_MS);
}

/** Shift a calendar date by a whole number of days (negative is earlier). UTC. */
export function addCalendarDays(iso: Iso, days: number): string {
  return new Date(Date.parse(`${dateOf(iso)}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

/** "Thu 10 Sep": the form the mocks use for the WHEN column. */
export function fmtDate(iso: Iso): string {
  const d = new Date(`${dateOf(iso)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** "3 Sep": the short form used inside a sentence. */
export function fmtDay(iso: Iso): string {
  const d = new Date(`${dateOf(iso)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** "Thu 10 Sep 2026": a calendar date standing on its own, where being a year out would mislead. */
export function fmtDateFull(iso: Iso): string {
  const y = yearOf(iso);
  return y === undefined ? iso : `${fmtDate(iso)} ${y}`;
}

/**
 * "10 Sep 2026": the same, without the weekday. A period of performance runs for years, where the
 * weekday is noise and the year is the whole point.
 */
export function fmtDayFull(iso: Iso): string {
  const y = yearOf(iso);
  return y === undefined ? iso : `${fmtDay(iso)} ${y}`;
}

function yearOf(iso: Iso): number | undefined {
  const d = new Date(`${dateOf(iso)}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? undefined : d.getUTCFullYear();
}

/** "16:10", in UTC. */
export function fmtTime(iso: Iso): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
}

/**
 * The zone a string is being rendered in: the viewer's own in a browser, the host's on a server.
 * Core builds sentences for the web and for Teams, so the caller decides; nothing here guesses.
 */
export function displayZone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
}

/**
 * "Thu 10 Sep 2026, 16:10 UTC": an operational timestamp, with the zone always named. Read against
 * the reader's own clock, so `tz` defaults to wherever this is running; core passes "UTC" explicitly
 * because a string it builds on the server is UTC and should say so rather than look local.
 */
export function fmtStamp(iso: Iso, tz: string = displayZone()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const parts = stampFormat(tz).formatToParts(d);
  const at = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
  return `${at("weekday")} ${at("day")} ${at("month")} ${at("year")}, ${at("hour")}:${at("minute")} ${at("timeZoneName")}`;
}

const STAMP_OPTS: Intl.DateTimeFormatOptions = {
  weekday: "short", day: "numeric", month: "short", year: "numeric",
  hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZoneName: "short",
};

// Building a formatter is the expensive half, and these lists render a stamp a row. An unknown zone
// throws rather than falling back, so it is caught once here instead of at every call site.
const STAMP_CACHE = new Map<string, Intl.DateTimeFormat>();
function stampFormat(tz: string): Intl.DateTimeFormat {
  const held = STAMP_CACHE.get(tz);
  if (held) return held;
  let made: Intl.DateTimeFormat;
  // en-US, not en-GB: en-GB abbreviates September "Sept", which would not match `fmtDate` beside it,
  // and names the zone "GMT-5" where a reader in Chicago expects "CDT".
  try { made = new Intl.DateTimeFormat("en-US", { ...STAMP_OPTS, timeZone: tz }); }
  catch { made = new Intl.DateTimeFormat("en-US", { ...STAMP_OPTS, timeZone: "UTC" }); }
  STAMP_CACHE.set(tz, made);
  return made;
}

/**
 * An ISO-8601 duration (the model's `duration` parameters: P1D, PT8H, P45D) in milliseconds. Months and
 * years are not accepted because the model never uses them and their length is not a number.
 */
export function parseDuration(iso: string): number {
  const m = /^P(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/.exec(iso.trim());
  if (!m) throw new Error(`not an ISO-8601 duration: ${iso}`);
  const [, w, d, h, min, s] = m;
  return (Number(w ?? 0) * 7 + Number(d ?? 0)) * DAY_MS + Number(h ?? 0) * 3_600_000 + Number(min ?? 0) * 60_000 + Number(s ?? 0) * 1000;
}

/** "1 day", "8 h", "45 min": the standard as a person would say it. */
export function fmtDuration(ms: number): string {
  if (ms >= DAY_MS && ms % DAY_MS === 0) { const d = ms / DAY_MS; return `${d} day${d === 1 ? "" : "s"}`; }
  // Nobody reads an hour count past a couple of days: "254.6 h" is a week and a half.
  if (ms >= 2 * DAY_MS) { const d = Math.round(ms / DAY_MS * 10) / 10; return `${d} days`; }
  if (ms >= 3_600_000) { const h = Math.round(ms / 3_600_000 * 10) / 10; return `${h} h`; }
  return `${Math.max(1, Math.round(ms / 60_000))} min`;
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * Capitalise a fragment that has landed at the start of a line: a `plural` result, a kind label. The
 * first character only, so an initialism and an identifier come back as they went in
 * (`docs/2026-09-28-displayed-text.md`, decisions 2 and 4). Never apply it to a key, a UPN or
 * anything on its way to the far side.
 */
export function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen", "twenty"];

/** "three", "eleven", "42": for the headline sentence, where digits under twenty read as shouting. */
export function numberWord(n: number): string {
  return n >= 0 && n < WORDS.length ? WORDS[n]! : String(n);
}
